import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import DemoWidget from '../components/DemoWidget';
import { WORKERS } from '../workerConfig';
import { fetchJson } from '../api';

// ── Edge API (Workers) ─────────────────────────────────────────────
function EdgeAPIDemo() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<null | object>(null);
  const [error, setError] = useState<string | null>(null);
  const [endpoint, setEndpoint] = useState<'/info' | '/geo' | '/headers'>('/info');

  const invoke = async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const data = await fetchJson<object>(`${WORKERS.edgeApi}${endpoint}`);
      setResult(data);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Worker unreachable');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: 'var(--cf-muted)', marginBottom: '1rem' }}>
        A Worker running at the edge returns real request metadata — colo, TLS version, ASN, geolocation.
        No origin server involved.
      </p>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {(['/info', '/geo', '/headers'] as const).map((ep) => (
          <button key={ep} className={`btn btn-sm ${endpoint === ep ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setEndpoint(ep)}>
            GET {ep}
          </button>
        ))}
      </div>
      <button className="btn btn-primary" onClick={invoke} disabled={loading} style={{ marginBottom: '1.25rem' }}>
        {loading ? <><span className="spinner" /> Calling Worker...</> : `Fetch ${endpoint}`}
      </button>
      {error && <div className="result-box result-error">{error}</div>}
      {result && <pre>{JSON.stringify(result, null, 2)}</pre>}
    </div>
  );
}

// ── KV Store ───────────────────────────────────────────────────────
function KVDemo() {
  const [key, setKey] = useState('demo:user:alice');
  const [value, setValue] = useState('{"theme":"dark","lang":"en","notifications":true}');
  const [readKey, setReadKey] = useState('');
  const [readResult, setReadResult] = useState<unknown>(null);
  const [keyList, setKeyList] = useState<string[]>([]);
  const [op, setOp] = useState<'write' | 'read' | 'list'>('write');
  const [loading, setLoading] = useState(false);
  const [lastOp, setLastOp] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const seed = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJson<{ seeded: number; keys: string[] }>(`${WORKERS.kvDemo}/seed`, { method: 'POST' });
      setKeyList(data.keys ?? []);
      setLastOp(`Seeded ${data.seeded} keys`);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Seed failed');
    } finally {
      setLoading(false);
    }
  };

  const write = async () => {
    if (!key) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJson<{ ok: boolean; written_at: string }>(`${WORKERS.kvDemo}/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, value, ttl: 3600 }),
      });
      setLastOp(`PUT ${key} → stored (${value.length} bytes) at ${data.written_at}`);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Write failed');
    } finally {
      setLoading(false);
    }
  };

  const read = async () => {
    if (!readKey) return;
    setLoading(true);
    setReadResult(null);
    setError(null);
    try {
      const res = await fetch(`${WORKERS.kvDemo}/get/${encodeURIComponent(readKey)}`);
      if (res.status === 404) {
        setLastOp(`GET ${readKey} → null (key not found)`);
      } else {
        const data = await res.json() as { key: string; value: unknown };
        setReadResult(data.value);
        setLastOp(`GET ${readKey} → HIT`);
      }
    } catch {
      setError('Read failed');
    } finally {
      setLoading(false);
    }
  };

  const listKeys = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJson<{ keys: Array<{ name: string }> }>(`${WORKERS.kvDemo}/keys?prefix=demo:`);
      setKeyList(data.keys.map((k) => k.name));
      setLastOp(`Listed ${data.keys.length} keys`);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'List failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: 'var(--cf-muted)', marginBottom: '1rem' }}>
        Read and write key-value pairs to Workers KV — a globally replicated, eventually consistent edge store.
      </p>

      <div className="toggle" style={{ marginBottom: '1rem' }}>
        <button className={`toggle-opt${op === 'write' ? ' active' : ''}`} onClick={() => setOp('write')}>Write</button>
        <button className={`toggle-opt${op === 'read' ? ' active' : ''}`} onClick={() => setOp('read')}>Read</button>
        <button className={`toggle-opt${op === 'list' ? ' active' : ''}`} onClick={() => setOp('list')}>List Keys</button>
      </div>

      {op === 'write' && (
        <>
          <div className="form-group">
            <label className="form-label">Key</label>
            <input className="form-input" value={key} onChange={(e) => setKey(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Value (string / JSON)</label>
            <textarea className="form-textarea" value={value} onChange={(e) => setValue(e.target.value)} rows={3} />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-primary" onClick={write} disabled={loading || !key}>
              {loading ? <><span className="spinner" /> Writing...</> : 'kv.put(key, value)'}
            </button>
            <button className="btn btn-secondary" onClick={seed} disabled={loading}>
              {loading ? <span className="spinner" /> : 'Seed demo data'}
            </button>
          </div>
        </>
      )}

      {op === 'read' && (
        <>
          <div className="form-group">
            <label className="form-label">Key to read</label>
            <input className="form-input" value={readKey} onChange={(e) => setReadKey(e.target.value)} placeholder="e.g. demo:greeting" />
          </div>
          {keyList.length > 0 && (
            <p style={{ fontSize: '0.78rem', color: 'var(--cf-muted)', marginBottom: '0.75rem' }}>
              Known keys:{' '}
              {keyList.slice(0, 5).map((k) => (
                <code key={k} style={{ marginRight: 6, cursor: 'pointer', color: 'var(--cf-blue)' }} onClick={() => setReadKey(k)}>{k}</code>
              ))}
            </p>
          )}
          <button className="btn btn-primary" onClick={read} disabled={loading || !readKey}>
            {loading ? <><span className="spinner" /> Reading...</> : 'kv.get(key)'}
          </button>
          {readResult !== null && (
            <div className="result-box result-success" style={{ marginTop: '1rem' }}>
              <strong>Value:</strong>
              <pre style={{ marginTop: '0.5rem' }}>{typeof readResult === 'string' ? readResult : JSON.stringify(readResult, null, 2)}</pre>
            </div>
          )}
        </>
      )}

      {op === 'list' && (
        <>
          <button className="btn btn-primary" onClick={listKeys} disabled={loading}>
            {loading ? <><span className="spinner" /> Listing...</> : 'kv.list({ prefix: "demo:" })'}
          </button>
          {keyList.length > 0 && (
            <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {keyList.map((k) => (
                <div key={k} style={{ fontFamily: 'monospace', fontSize: '0.83rem', padding: '0.3rem 0.5rem', background: 'var(--cf-bg)', borderRadius: 4, border: '1px solid var(--cf-border)' }}>
                  {k}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {error && <div className="result-box result-error" style={{ marginTop: '1rem' }}>{error}</div>}
      {lastOp && (
        <div className="result-box result-info" style={{ marginTop: '1rem', fontSize: '0.82rem' }}>
          <code>{lastOp}</code>
        </div>
      )}
    </div>
  );
}

// ── D1 SQL at the Edge ──────────────────────────���──────────────────
function D1Demo() {
  const [query, setQuery] = useState('SELECT * FROM users');
  const [running, setRunning] = useState(false);
  const [queryResult, setQueryResult] = useState<object[] | null>(null);
  const [queryTime, setQueryTime] = useState<number | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  const initDb = async () => {
    setRunning(true);
    setError(null);
    setStatus(null);
    try {
      const data = await fetchJson<{ ok: boolean; users: object[] }>(`${WORKERS.d1Demo}/init`, { method: 'POST' });
      setInitialized(true);
      setQueryResult(data.users);
      setQueryTime(null);
      setStatus(`Database initialized — ${data.users.length} users seeded`);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Init failed');
    } finally {
      setRunning(false);
    }
  };

  const runQuery = async () => {
    setRunning(true);
    setError(null);
    const start = Date.now();
    try {
      const data = await fetchJson<{ results: object[] }>(`${WORKERS.d1Demo}/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sql: query }),
      });
      setQueryResult(data.results);
      setQueryTime(Date.now() - start);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Query failed');
    } finally {
      setRunning(false);
    }
  };

  const addUser = async () => {
    setRunning(true);
    setError(null);
    const names = ['Eva Liu', 'Frank Torres', 'Grace Kim', 'Hiro Sato'];
    const name = names[Math.floor(Math.random() * names.length)];
    const email = `${name.split(' ')[0].toLowerCase()}${Math.floor(Math.random() * 9000 + 1000)}@demo.cf`;
    try {
      await fetchJson(`${WORKERS.d1Demo}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, role: 'viewer' }),
      });
      setStatus(`Inserted: ${name} (${email})`);
      setQuery('SELECT * FROM users');
      // Re-run query to show updated table
      const start = Date.now();
      const data = await fetchJson<{ results: object[] }>(`${WORKERS.d1Demo}/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sql: 'SELECT * FROM users' }),
      });
      setQueryResult(data.results);
      setQueryTime(Date.now() - start);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Insert failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: 'var(--cf-muted)', marginBottom: '1rem' }}>
        D1 is SQLite at the edge. Write SQL, run it from a Worker, get results at single-digit millisecond latency.
      </p>

      {!initialized && (
        <div className="result-box result-info" style={{ marginBottom: '1rem', fontSize: '0.85rem' }}>
          First run: click <strong>Init Database</strong> to create tables and seed data.
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <button className="btn btn-secondary btn-sm" onClick={() => setQuery('SELECT * FROM users')}>All users</button>
        <button className="btn btn-secondary btn-sm" onClick={() => setQuery("SELECT * FROM users WHERE role = 'admin'")}>Admins</button>
        <button className="btn btn-secondary btn-sm" onClick={() => setQuery('SELECT * FROM events ORDER BY id DESC LIMIT 5')}>Recent events</button>
        <button className="btn btn-secondary btn-sm" onClick={addUser} disabled={!initialized || running}>+ Insert user</button>
        {!initialized && (
          <button className="btn btn-primary btn-sm" onClick={initDb} disabled={running}>
            {running ? <span className="spinner" /> : 'Init database'}
          </button>
        )}
      </div>

      <textarea
        className="form-textarea"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        rows={2}
        style={{ fontFamily: 'monospace', fontSize: '0.85rem', marginBottom: '0.75rem' }}
      />

      <button className="btn btn-primary" onClick={runQuery} disabled={running}>
        {running ? <><span className="spinner" /> Running query...</> : 'Run Query'}
      </button>

      {error && <div className="result-box result-error" style={{ marginTop: '1rem' }}>{error}</div>}
      {status && <div className="result-box result-success" style={{ marginTop: '1rem', fontSize: '0.83rem' }}>{status}</div>}

      {queryResult !== null && !error && (
        <div style={{ marginTop: '1rem' }}>
          {queryTime !== null && (
            <div style={{ fontSize: '0.78rem', color: 'var(--cf-muted)', marginBottom: '0.5rem' }}>
              {queryResult.length} row(s) — {queryTime}ms round-trip
            </div>
          )}
          {queryResult.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: 'var(--cf-bg)' }}>
                    {Object.keys(queryResult[0]).map((col) => (
                      <th key={col} style={{ textAlign: 'left', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--cf-border)', fontWeight: 700, fontSize: '0.75rem', color: 'var(--cf-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {queryResult.map((row: any, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--cf-border)' }}>
                      {Object.values(row).map((val: any, j) => (
                        <td key={j} style={{ padding: '0.5rem 0.75rem', color: 'var(--cf-dark)' }}>{String(val)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="result-box result-info">No rows matched.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Hyperdrive Latency Comparison ──────────────────────────────────
function HyperdriveDemo() {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<null | { direct: number; hyperdrive: number }>(null);

  const run = () => {
    setRunning(true);
    setResults(null);
    setTimeout(() => {
      setResults({
        direct: Math.floor(Math.random() * 80) + 140,
        hyperdrive: Math.floor(Math.random() * 8) + 4,
      });
      setRunning(false);
    }, 2000);
  };

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: 'var(--cf-muted)', marginBottom: '1rem' }}>
        Illustrative side-by-side comparison of direct database connections and Hyperdrive.
        The current values are generated samples, not measured database requests.
      </p>
      <button className="btn btn-primary" onClick={run} disabled={running}>
        {running ? <><span className="spinner" /> Running benchmark (2s)...</> : 'Run Latency Comparison'}
      </button>

      {results && (
        <div style={{ marginTop: '1.25rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div style={{ background: 'var(--cf-danger-light)', border: '1px solid #fecaca', borderRadius: 'var(--radius-sm)', padding: '1.125rem', textAlign: 'center' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--cf-danger)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.25rem' }}>Direct to Postgres</div>
            <div style={{ fontSize: '2.25rem', fontWeight: 900, color: 'var(--cf-danger)' }}>{results.direct}ms</div>
            <div style={{ fontSize: '0.75rem', color: '#7f1d1d', marginTop: '0.25rem' }}>Worker → GCP us-central1</div>
            <div style={{ fontSize: '0.72rem', color: '#7f1d1d', marginTop: '0.375rem' }}>New TCP connection + TLS + query</div>
          </div>
          <div style={{ background: 'var(--cf-success-light)', border: '1px solid #a7f3d0', borderRadius: 'var(--radius-sm)', padding: '1.125rem', textAlign: 'center' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--cf-success)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.25rem' }}>Via Hyperdrive</div>
            <div style={{ fontSize: '2.25rem', fontWeight: 900, color: 'var(--cf-success)' }}>{results.hyperdrive}ms</div>
            <div style={{ fontSize: '0.75rem', color: '#064e3b', marginTop: '0.25rem' }}>Worker → Hyperdrive → DB</div>
            <div style={{ fontSize: '0.72rem', color: '#064e3b', marginTop: '0.375rem' }}>Pooled connection + regional cache</div>
          </div>
          <div className="result-box result-info" style={{ gridColumn: 'span 2' }}>
            <strong>{(results.direct / results.hyperdrive).toFixed(0)}× faster</strong> with Hyperdrive.
            Hyperdrive maintains persistent connection pools to your database from regional Cloudflare
            infrastructure, eliminating TCP+TLS handshake overhead on every Worker invocation.
          </div>
        </div>
      )}
    </div>
  );
}

// ── R2 Object Storage ──────────────────────────────────────────────
function R2Demo() {
  const [files, setFiles] = useState<Array<{ key: string; size: number; uploaded: string }>>([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [fileContent, setFileContent] = useState('Hello from Cloudflare R2!');
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [seeded, setSeeded] = useState(false);

  const seed = async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchJson(`${WORKERS.r2Demo}/seed`, { method: 'POST' });
      const data = await fetchJson<{ objects: Array<{ key: string; size: number; uploaded: string }> }>(`${WORKERS.r2Demo}/list?prefix=demo/`);
      setFiles(data.objects ?? []);
      setSeeded(true);
      setStatus('Bucket seeded with sample files');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Seed failed');
    } finally {
      setLoading(false);
    }
  };

  const listFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJson<{ objects: Array<{ key: string; size: number; uploaded: string }> }>(`${WORKERS.r2Demo}/list?prefix=demo/`);
      setFiles(data.objects ?? []);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'List failed');
    } finally {
      setLoading(false);
    }
  };

  const upload = async () => {
    if (!fileName.trim()) return;
    setUploading(true);
    setError(null);
    try {
      await fetchJson(`${WORKERS.r2Demo}/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: fileName, content: fileContent }),
      });
      setStatus(`Uploaded: ${fileName}`);
      setFileName('');
      setFileContent('Hello from Cloudflare R2!');
      await listFiles();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const deleteFile = async (key: string) => {
    setError(null);
    try {
      await fetchJson(`${WORKERS.r2Demo}/delete/${encodeURIComponent(key)}`, { method: 'DELETE' });
      setFiles((f) => f.filter((x) => x.key !== key));
      setStatus(`Deleted: ${key}`);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Delete failed');
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: 'var(--cf-muted)', marginBottom: '1rem' }}>
        R2 stores objects with <strong>zero egress fees</strong>. Upload an object, list the bucket,
        delete files, all through a Worker binding.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {!seeded && (
          <button className="btn btn-secondary" onClick={seed} disabled={loading}>
            {loading ? <span className="spinner" /> : 'Seed sample files'}
          </button>
        )}
        <button className="btn btn-secondary" onClick={listFiles} disabled={loading}>
          {loading ? <><span className="spinner" /> Listing...</> : 'List bucket'}
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
        <input
          className="form-input"
          placeholder="Object key (e.g. demo/myfile.txt)"
          value={fileName}
          onChange={(e) => setFileName(e.target.value)}
        />
        <textarea
          className="form-textarea"
          placeholder="File content..."
          value={fileContent}
          onChange={(e) => setFileContent(e.target.value)}
          rows={2}
        />
        <button className="btn btn-primary" onClick={upload} disabled={uploading || !fileName.trim()} style={{ alignSelf: 'flex-start' }}>
          {uploading ? <><span className="spinner" /> Uploading...</> : 'Upload to R2'}
        </button>
      </div>

      {error && <div className="result-box result-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
      {status && <div className="result-box result-success" style={{ marginBottom: '0.75rem', fontSize: '0.83rem' }}>{status}</div>}

      {files.length > 0 && (
        <div style={{ border: '1px solid var(--cf-border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
          <div style={{ background: 'var(--cf-bg)', padding: '0.5rem 0.875rem', fontSize: '0.75rem', fontWeight: 700, color: 'var(--cf-muted)', display: 'grid', gridTemplateColumns: '1fr 70px 100px 60px', gap: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <span>Key</span><span>Size</span><span>Uploaded</span><span></span>
          </div>
          {files.map((f) => (
            <div key={f.key} style={{ padding: '0.625rem 0.875rem', fontSize: '0.85rem', display: 'grid', gridTemplateColumns: '1fr 70px 100px 60px', gap: '0.5rem', borderTop: '1px solid var(--cf-border)', alignItems: 'center' }}>
              <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.key}</span>
              <span style={{ color: 'var(--cf-muted)', fontSize: '0.8rem' }}>{formatBytes(f.size)}</span>
              <span style={{ color: 'var(--cf-muted)', fontSize: '0.78rem' }}>{new Date(f.uploaded).toLocaleTimeString()}</span>
              <button className="btn btn-ghost btn-sm" style={{ padding: '0.2rem 0.4rem', color: 'var(--cf-danger)', fontSize: '0.78rem' }} onClick={() => deleteFile(f.key)}>
                Delete
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="result-box result-info" style={{ marginTop: '1rem', fontSize: '0.82rem' }}>
        Files served via Worker — zero egress fees. S3 charges $0.09/GB out. R2 charges <strong>$0</strong>.
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────
export default function DevPlatformPage() {
  return (
    <main className="main">
      <div className="demo-page">
        <div className="demo-page-header">
          <div className="breadcrumb">
            <Link to="/">Portal</Link>
            <span className="breadcrumb-sep">/</span>
            <span>Developer Platform</span>
          </div>
          <h2>Developer Platform Demos</h2>
          <p>
            Interactive Workers, D1, KV, and R2 demos, plus a clearly labeled Hyperdrive comparison prototype.
          </p>
        </div>

        <div className="demo-list">
          <DemoWidget
            icon="⚙️"
            title="Edge API — Workers"
            product="Workers"
            script="Click the button. Show the colo and the TLS cipher. Then say: this runs on every Cloudflare edge server in 300 cities. Sub-millisecond cold starts. No servers to manage."
          >
            <EdgeAPIDemo />
          </DemoWidget>

          <DemoWidget
            icon="🗄️"
            title="KV Store — Global Edge Cache"
            product="Workers KV"
            script="Seed demo data first, then read a key back. Explain: KV replicates to every PoP — a user in Tokyo reads the same data as one in NYC with sub-ms latency. Best for config, sessions, feature flags."
          >
            <KVDemo />
          </DemoWidget>

          <DemoWidget
            icon="🗃️"
            title="D1 — SQL at the Edge"
            product="D1 (SQLite)"
            script="Click Init Database first, then run the SELECT query. Point at the round-trip time. D1 is SQLite, so customers already know the API. No new ORM. Full SQL at zero infra overhead."
          >
            <D1Demo />
          </DemoWidget>

          <DemoWidget
            icon="⚡"
            title="Hyperdrive Comparison Prototype"
            product="Illustrative"
            script="This previews the intended benchmark UI with generated values. A live Hyperdrive and database integration still needs to be connected."
          >
            <HyperdriveDemo />
          </DemoWidget>

          <DemoWidget
            icon="🪣"
            title="R2 Object Storage"
            product="R2"
            script="Seed the bucket, then list it. Upload a file. Show it appear in the list. Then say: S3 charges $0.09/GB egress. R2 charges zero. For a media company serving 1TB/day that's $90/day in savings — instant."
          >
            <R2Demo />
          </DemoWidget>
        </div>
      </div>
    </main>
  );
}
